// Phase-A R1.2 — Breadcrumb row. WORKSPACE-only, 36px tall.
// At <md (<768) and >3 crumbs: truncate to first › … › last-2 with a
// Radix Popover on the … that shows the hidden hops as clickable links.
// S-21 — the "future resolver hook" promised in breadcrumb-map.ts landed:
// for entity routes the raw URL param (a cuid) resolves to the entity's
// TITLE (course title, exam title, session title) via a module-level cache
// (one fetch per entity per SPA lifetime; failure caches the raw fallback).
import React from "react";
import * as Popover from "@radix-ui/react-popover";
import { assessmentsApi, catalogApi, classSessionsApi } from "../api/endpoints.js";
import { useAuth } from "../auth/AuthContext";
import { useCurrentRoute, useGo } from "../router";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { breadcrumbLabel } from "../router/breadcrumb-map";

interface Crumb { label: string; routeId?: string; isLast: boolean }
interface Ellipsis { ellipsis: true; hidden: Crumb[] }

// route-id → async param→title resolver. Only entity routes are listed;
// everything else keeps the static breadcrumbLabel(param) fallback.
const PARAM_RESOLVERS: Record<string, (param: string) => Promise<string | null>> = {
  course: async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  "course-live": async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  gradebook: async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  exam: async (p) => ((await assessmentsApi.get(p)) as { title?: string }).title ?? null,
  "assessment-live": async (p) => ((await assessmentsApi.get(p)) as { title?: string }).title ?? null,
  "live-class": async (p) => {
    // param may carry a sub-path (<id>/report | <id>/lobby).
    const [id, sub] = p.split("/");
    const s = (await classSessionsApi.get(id)) as { title?: string };
    if (!s.title) return null;
    return sub === "report" ? s.title + " · گزارش" : sub === "lobby" ? s.title + " · لابی" : s.title;
  },
};

const paramLabelCache = new Map<string, string>();
// S-21 review fix — the cache must never outlive the auth IDENTITY: logout is
// a pure SPA transition (no reload), so a bare module-level Map would serve
// user A's resolved titles to user B in the same tab (cross-user/tenant title
// disclosure — server no-leak-404 never re-runs on a cache hit), and a denied
// user's negative "" entry would stick for a later authorized user. The cache
// is therefore owned by one tenant:user identity and cleared on any change.
let cacheOwner = "";
const ensureCacheOwner = (owner: string): void => {
  if (owner !== cacheOwner) {
    cacheOwner = owner;
    paramLabelCache.clear();
  }
};

/** Resolved entity title for the param crumb, or null → raw fallback. */
const useBreadcrumbParamLabel = (routeId: string, param: string | null, owner: string): string | null => {
  // Must run BEFORE the useState initializer reads the cache.
  ensureCacheOwner(owner);
  const key = routeId + ":" + (param ?? "");
  const [label, setLabel] = React.useState<string | null>(() =>
    param ? paramLabelCache.get(key) || null : null,
  );
  React.useEffect(() => {
    if (!param || !PARAM_RESOLVERS[routeId]) {
      setLabel(null);
      return;
    }
    const cached = paramLabelCache.get(key);
    if (cached !== undefined) {
      setLabel(cached || null);
      return;
    }
    let alive = true;
    PARAM_RESOLVERS[routeId](param)
      .then((t) => {
        paramLabelCache.set(key, t ?? "");
        if (alive) setLabel(t ?? null);
      })
      .catch(() => {
        // Cache the failure too — a 404/denied entity must not refetch on
        // every breadcrumb render.
        paramLabelCache.set(key, "");
        if (alive) setLabel(null);
      });
    return () => {
      alive = false;
    };
  }, [key, routeId, param]);
  return label;
};

const Sep: React.FC = () => <span aria-hidden="true" className="breadcrumb-sep">›</span>;

export const Breadcrumbs: React.FC = () => {
  const { id: route, param } = useCurrentRoute();
  const go = useGo();
  const isMd = useMediaQuery("(min-width: 768px)");
  const { user } = useAuth();
  const resolvedParamLabel = useBreadcrumbParamLabel(
    route,
    param,
    (user?.tenantId ?? "") + ":" + (user?.userId ?? ""),
  );

  const crumbs: Crumb[] = [];
  if (route !== "home" && route !== "") {
    crumbs.push({ label: breadcrumbLabel("home"), routeId: "home", isLast: false });
  }
  if (route && route !== "home") {
    crumbs.push({ label: breadcrumbLabel(route), routeId: route, isLast: !param });
  }
  if (param) crumbs.push({ label: resolvedParamLabel ?? breadcrumbLabel(param), isLast: true });
  if (crumbs.length === 0) crumbs.push({ label: breadcrumbLabel("home"), isLast: true });

  const shouldTruncate = !isMd && crumbs.length > 3;
  const items: Array<Crumb | Ellipsis> = shouldTruncate
    ? [crumbs[0], { ellipsis: true, hidden: crumbs.slice(1, -2) }, ...crumbs.slice(-2)]
    : crumbs;

  return (
    <nav aria-label="مسیر صفحه" className="breadcrumb-row">
      <ol>
        {items.map((c, i) => {
          const isTail = i === items.length - 1;
          if ("ellipsis" in c) {
            return (
              <li key={`e-${i}`} className="breadcrumb-ellipsis">
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <button type="button" aria-label="نمایش مسیر کامل">…</button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content className="breadcrumb-popover" sideOffset={4} align="start">
                      <ul>
                        {c.hidden.map((h, j) => (
                          <li key={j}>
                            <button type="button" onClick={() => h.routeId && go(h.routeId)}>{h.label}</button>
                          </li>
                        ))}
                      </ul>
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
                {!isTail && <Sep />}
              </li>
            );
          }
          return (
            <li key={i}>
              {c.isLast || !c.routeId
                ? <span aria-current="page">{c.label}</span>
                : <a href={"/" + c.routeId} onClick={(e) => { e.preventDefault(); go(c.routeId!); }}>{c.label}</a>}
              {!isTail && <Sep />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
